// import the css file so rollup is aware of it
import './css/global.css';

import '@hotwired/turbo';
import { Application } from '@hotwired/stimulus';
import { WelcomeVideoController } from '@controllers/welcome-video';
import { ContactFormController } from '@controllers/contact-form';
import { DrawerController } from '@controllers/drawer';
import {FlashMessageController} from "@controllers/flash-message";
import {SignupFormController} from "@controllers/signup-form";
import {ModalController} from "@controllers/modal";
import {AnalysisFormController} from "@controllers/analysis-form";
import {NavigationController} from "@controllers/navigation";
import {LogoutController} from "@controllers/logout";
import {AnalysisResultsController} from "@controllers/analysis-results";
import {QuickCheckController} from "@controllers/quick-check";
import {ManualEntryController} from "@controllers/manual-entry";
import {UserAccountController} from "@controllers/user-account";
import {UserProfileController} from "@controllers/user-profile";
import {FoodDiaryController} from "@controllers/food-diary";
import {AdminDashboardController} from "@controllers/admin-dashboard";
import {QuickActionsController} from "@controllers/quick-actions";
import {InstallPwaController} from "@controllers/install-pwa";
import {HeroStepsController} from "@controllers/hero-steps";
import {LocalDateTimeController} from "@controllers/local-datetime";
import {LandingAnimationsController} from "@controllers/landing-animations";
import {BuddyReorderController} from "@controllers/buddy-reorder";
import {WebPushController} from "@controllers/web-push";
import {PushNotificationBannerController} from "@controllers/push-notification-banner";
import {WorkoutBuilderController} from "@controllers/workout-builder";
import {AiLoadingController} from "@controllers/ai-loading";
import {FormSubmitController} from "@controllers/form-submit";
import {WelcomeSetupController} from "@controllers/welcome-setup";
import {WorkoutSessionController} from "@controllers/workout-session";
import {QueuedMealController} from "@controllers/queued-meal";
import {ensureFormCsrfToken} from './services/form.services';

declare global {
    interface Window {
        Stimulus: Application;
        Turbo?: unknown;
    }
}

console.info('initializing app', window.Turbo);
window.Stimulus = Application.start();
const Stimulus = window.Stimulus;
console.info('Stimulus started', Stimulus);

const resetTransientUiState = () => {
    document.body.classList.remove('overflow-hidden', 'food-diary-drag-active', 'buddy-reorder-active', 'workout-builder-drag-active');

    for (const dialog of document.querySelectorAll<HTMLDialogElement>('dialog[open]')) {
        dialog.close();
    }

    for (const form of document.querySelectorAll<HTMLFormElement>('form[aria-busy="true"]')) {
        form.removeAttribute('aria-busy');
    }

    for (const button of document.querySelectorAll<HTMLButtonElement>('button[disabled][data-turbo-reset-disabled]')) {
        button.disabled = false;
        button.classList.remove('opacity-75', 'cursor-not-allowed');
        if (button.dataset.originalText) {
            button.textContent = button.dataset.originalText;
        }
        delete button.dataset.turboResetDisabled;
    }

    for (const loading of document.querySelectorAll<HTMLElement>('[data-turbo-reset-loading]')) {
        loading.classList.add('hidden');
        loading.classList.remove('inline-flex');
        loading.style.removeProperty('display');
        delete loading.dataset.turboResetLoading;
    }

    for (const label of document.querySelectorAll<HTMLElement>('[data-turbo-reset-label]')) {
        label.classList.remove('hidden');
        label.style.removeProperty('display');
        delete label.dataset.turboResetLabel;
    }

    window.dispatchEvent(new CustomEvent('ovasai:quick-actions:reset'));
};

const setMobileMenuOpen = (nav: HTMLElement, open: boolean) => {
    const menu = nav.querySelector<HTMLElement>('[data-navigation-target~="mobileMenu"]');
    const button = nav.querySelector<HTMLElement>('[data-navigation-target~="mobileMenuButton"]');
    const openIcon = nav.querySelector<HTMLElement>('[data-navigation-target~="mobileMenuOpenIcon"]');
    const closeIcon = nav.querySelector<HTMLElement>('[data-navigation-target~="mobileMenuCloseIcon"]');

    menu?.classList.toggle('hidden', !open);
    openIcon?.classList.toggle('hidden', open);
    openIcon?.classList.toggle('block', !open);
    closeIcon?.classList.toggle('hidden', !open);
    closeIcon?.classList.toggle('block', open);
    button?.setAttribute('aria-expanded', String(open));
};

const setQuickActionsOpen = (nav: HTMLElement, open: boolean) => {
    nav.classList.toggle('quick-actions-open', open);
    nav.querySelector<HTMLElement>('[data-quick-actions-target~="trigger"]')?.setAttribute('aria-expanded', String(open));

    for (const action of nav.querySelectorAll<HTMLElement>('[data-quick-actions-target~="action"]')) {
        action.style.transitionDelay = open ? `${30 + Array.from(nav.querySelectorAll('[data-quick-actions-target~="action"]')).indexOf(action) * 45}ms` : '0ms';
        if (!open) {
            action.classList.remove('quick-action-item-selected');
        }
    }
};

const resetCriticalNavigationControls = () => {
    for (const nav of document.querySelectorAll<HTMLElement>('[data-controller~="navigation"]')) {
        setMobileMenuOpen(nav, false);
    }

    for (const nav of document.querySelectorAll<HTMLElement>('[data-controller~="quick-actions"]')) {
        setQuickActionsOpen(nav, false);
    }
};

let activeBuddyMotivationForm: HTMLFormElement | null = null;

const handleBuddyMotivationClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) {
        return;
    }

    const dialog = document.getElementById('buddy-motivation-modal') as HTMLDialogElement | null;
    const message = document.querySelector<HTMLTextAreaElement>('[data-buddy-motivation-message]');
    const trigger = target.closest<HTMLButtonElement>('[data-buddy-motivation-trigger]');
    if (trigger) {
        if (trigger.disabled) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        activeBuddyMotivationForm = trigger.closest<HTMLFormElement>('form');
        const existingInput = activeBuddyMotivationForm?.querySelector<HTMLInputElement>('input[name="message"]');
        if (message) {
            message.value = existingInput?.value
                || trigger.dataset.defaultMessage
                || "you're doing great!";
        }
        if (dialog?.showModal) {
            dialog.showModal();
            window.requestAnimationFrame(() => message?.focus());
        } else {
            activeBuddyMotivationForm?.requestSubmit();
        }
        return;
    }

    if (target.closest('[data-buddy-motivation-cancel]')) {
        event.preventDefault();
        dialog?.close();
        activeBuddyMotivationForm = null;
        return;
    }

    const sendButton = target.closest<HTMLButtonElement>('[data-buddy-motivation-send]');
    if (!sendButton) {
        return;
    }
    event.preventDefault();
    if (!activeBuddyMotivationForm) {
        dialog?.close();
        return;
    }

    const messageInput = activeBuddyMotivationForm.querySelector<HTMLInputElement>('input[name="message"]');
    if (messageInput && message) {
        messageInput.value = message.value.trim() || "you're doing great!";
    }
    sendButton.disabled = true;
    sendButton.textContent = 'Sending…';
    activeBuddyMotivationForm.setAttribute('aria-busy', 'true');
    dialog?.close();
    activeBuddyMotivationForm.requestSubmit();
};

const handleCriticalNavigationClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) {
        return;
    }

    const mobileMenuButton = target.closest<HTMLElement>('[data-navigation-target~="mobileMenuButton"]');
    if (mobileMenuButton) {
        const nav = mobileMenuButton.closest<HTMLElement>('[data-controller~="navigation"]');
        if (nav) {
            event.preventDefault();
            event.stopImmediatePropagation();
            setMobileMenuOpen(nav, mobileMenuButton.getAttribute('aria-expanded') !== 'true');
        }
        return;
    }

    const quickActionsTrigger = target.closest<HTMLElement>('[data-quick-actions-target~="trigger"]');
    if (quickActionsTrigger) {
        if (quickActionsTrigger.dataset.quickActionsSuppressClick === 'true') {
            event.preventDefault();
            event.stopImmediatePropagation();
            delete quickActionsTrigger.dataset.quickActionsSuppressClick;
            return;
        }

        const nav = quickActionsTrigger.closest<HTMLElement>('[data-controller~="quick-actions"]');
        if (nav) {
            event.preventDefault();
            event.stopImmediatePropagation();
            setQuickActionsOpen(nav, quickActionsTrigger.getAttribute('aria-expanded') !== 'true');
        }
        return;
    }

    for (const nav of document.querySelectorAll<HTMLElement>('[data-controller~="navigation"]')) {
        if (!nav.contains(target)) {
            setMobileMenuOpen(nav, false);
        }
    }

    for (const nav of document.querySelectorAll<HTMLElement>('[data-controller~="quick-actions"]')) {
        if (!nav.contains(target)) {
            setQuickActionsOpen(nav, false);
        }
    }
};

document.addEventListener('turbo:before-cache', resetTransientUiState);
document.addEventListener('turbo:before-render', resetTransientUiState);
document.addEventListener('turbo:render', resetTransientUiState);
document.addEventListener('turbo:load', resetTransientUiState);
document.addEventListener('turbo:before-cache', resetCriticalNavigationControls);
document.addEventListener('turbo:before-render', resetCriticalNavigationControls);
window.addEventListener('pageshow', resetTransientUiState);
window.addEventListener('pageshow', resetCriticalNavigationControls);
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
        resetTransientUiState();
        resetCriticalNavigationControls();
    }
});
document.addEventListener('click', handleBuddyMotivationClick);
document.addEventListener('click', handleCriticalNavigationClick, true);
document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form instanceof HTMLFormElement) {
        ensureFormCsrfToken(form);
    }
}, true);

Stimulus.register('welcome-video', WelcomeVideoController);
Stimulus.register('contact-form', ContactFormController);
Stimulus.register('drawer', DrawerController);
Stimulus.register('flash-message', FlashMessageController);
Stimulus.register('signup-form', SignupFormController);
Stimulus.register('modal', ModalController);
Stimulus.register('analysis-form', AnalysisFormController);
Stimulus.register('navigation', NavigationController);
Stimulus.register('logout', LogoutController);
Stimulus.register('analysis-results', AnalysisResultsController);
Stimulus.register('quick-check', QuickCheckController);
Stimulus.register('manual-entry', ManualEntryController);
Stimulus.register('user-account', UserAccountController);
Stimulus.register('user-profile', UserProfileController);
Stimulus.register('food-diary', FoodDiaryController);
Stimulus.register('admin-dashboard', AdminDashboardController);
Stimulus.register('quick-actions', QuickActionsController);
Stimulus.register('install-pwa', InstallPwaController);
Stimulus.register('hero-steps', HeroStepsController);
Stimulus.register('local-datetime', LocalDateTimeController);
Stimulus.register('landing-animations', LandingAnimationsController);
Stimulus.register('buddy-reorder', BuddyReorderController);
Stimulus.register('web-push', WebPushController);
Stimulus.register('push-notification-banner', PushNotificationBannerController);
Stimulus.register('workout-builder', WorkoutBuilderController);
Stimulus.register('ai-loading', AiLoadingController);
Stimulus.register('form-submit', FormSubmitController);
Stimulus.register('welcome-setup', WelcomeSetupController);
Stimulus.register('workout-session', WorkoutSessionController);
Stimulus.register('queued-meal', QueuedMealController);

if ('serviceWorker' in navigator) {
    navigator.serviceWorker
        .register('/static/service-worker.js', {scope: '/'})
        .then((registration) => {
            registration.update().catch((error: unknown) => {
                console.warn('service worker update check failed', error);
            });
            window.dispatchEvent(new CustomEvent('ovasai:pwa-service-worker-ready'));
        })
        .catch((error: unknown) => {
            console.error('service worker registration failed', error);
        });
}
